import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Sparkles, Brain, X, ArrowRight } from "lucide-react";

/**
 * Sticky top promo banner — invites every visitor to explore the
 * What-Is and AI-Confidence-Engine sections without revealing investor specifics.
 * On the landing page, smooth-scrolls to the inline section.
 * On other pages, deep-links to the standalone shareable route.
 * Dismissable per-session.
 */
export function ExploreBanner() {
  const [hidden, setHidden] = useState(() => sessionStorage.getItem("dgx_explore_banner_dismissed") === "1");
  const location = useLocation();
  const onLanding = location.pathname === "/";
  if (hidden) return null;

  const dismiss = () => {
    sessionStorage.setItem("dgx_explore_banner_dismissed", "1");
    setHidden(true);
  };

  const handleAnchor = (id) => (e) => {
    if (!onLanding) return; // Let <Link> route navigation happen normally on non-landing pages
    e.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div
      className="relative bg-gradient-to-r from-[#22C55E] via-[#1CAB4D] to-[#22C55E] text-[#050A07] text-xs font-semibold tracking-wider uppercase"
      data-testid="explore-banner"
    >
      <div className="max-w-[1400px] mx-auto px-6 pr-12 py-2.5 flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
        <span className="hidden sm:inline-flex items-center gap-1.5 opacity-90">
          <Sparkles className="w-3 h-3" /> Explore
        </span>
        <Link
          to="/what-is-delta-greenx"
          onClick={handleAnchor("what-is")}
          className="inline-flex items-center gap-1 hover:underline"
          data-testid="explore-banner-what-is"
        >
          What is Delta GreenX <ArrowRight className="w-3 h-3" />
        </Link>
        <span className="opacity-30">·</span>
        <Link
          to="/ai-confidence-engine"
          onClick={handleAnchor("ai-engine")}
          className="inline-flex items-center gap-1 hover:underline"
          data-testid="explore-banner-ai-engine"
        >
          <Brain className="w-3 h-3" /> The AI Confidence Engine <ArrowRight className="w-3 h-3" />
        </Link>
        <span className="hidden md:inline opacity-60 normal-case font-normal tracking-normal">
          — see how we power the operating infrastructure for global food trade
        </span>
        <button
          onClick={dismiss}
          className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-black/10 rounded"
          data-testid="explore-banner-dismiss"
          aria-label="Dismiss"
        >
          <X className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}
